import { useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { updateUser } from 'lane-shared/graphql/user';
import Storage, { StorageKeys } from 'lane-shared/helpers/Storage';
import { useUserDataContext } from 'lane-shared/hooks';
import { SWIRE_LANGUAGE_ENUM } from 'constants-whitelabel';

/**
 * Hook to handle post-login user update after Auth0 redirect.
 * Calls updateUser API with the user's selected language from localStorage.
 * 
 * @param enabled - Whether the hook should be enabled (default: true)
 * 
 * @example
 * ```tsx
 * import { useAuth0PostLoginUpdate } from 'react-hooks';
 * import { useSwireApp } from 'react-hooks';
 * 
 * const { whitelabel } = useContext(AppContext);
 * const { isSwire } = useSwireApp(whitelabel);
 * useAuth0PostLoginUpdate(isSwire);
 * ```
 */
export function useAuth0PostLoginUpdate(enabled: boolean = true) {
  const { isAuthenticated } = useAuth0();
  const { user, refetch: refetchUser } = useUserDataContext();
  const [updateUserMutation] = useMutation(updateUser);
  
  const refetchUserRef = useRef(refetchUser);

  useEffect(() => {
    refetchUserRef.current = refetchUser;
  }, [refetchUser]);
  
  const updateUserLanguage = useCallback(async () => {
    if (!enabled || !isAuthenticated || !user?._id) {
      return;
    }
    
    const storageLocale = await Storage.getItemMaybe(StorageKeys.USER_LOCALE_AT_LOGIN);

    const locale = Object.values(SWIRE_LANGUAGE_ENUM).find(e => e === storageLocale) ?? user?.locale;

    if (locale && locale !== user?.locale) {
      try {
        // useMutation returns a stable function
        await updateUserMutation({
          variables: {
            user: {
              _id: user._id,
              locale,
            },
          },
        });
        // delete the locale from storage after successful update
        await Storage.removeItem(StorageKeys.USER_LOCALE_AT_LOGIN);

        // Refetch user data to get the updated locale
        // Use ref to access latest refetch function without including it in dependencies
        if (refetchUserRef.current) {
          await refetchUserRef.current();
        }
      } catch (error) {
        // Log error but don't block the user flow
        console.error('Failed to update user language after Auth0 redirect:', error);
        // delete the locale from storage to prevent retry loops
        await Storage.removeItem(StorageKeys.USER_LOCALE_AT_LOGIN);
      }
    }
  }, [enabled, isAuthenticated, user?._id, user?.locale, updateUserMutation]);

  useEffect(() => {
    updateUserLanguage();
  }, [updateUserLanguage]);
}
